import { sessionExpired, showGenericError } from '../actions';
import app from '../app.json';
import {
  CLAIMS_API_TOKEN,
  CUSTOMER_API_TOKEN,
  POLICY_API_TOKEN,
  PURCHASE_API_TOKEN,
  SOCIAL_API_TOKEN,
} from './constants';
import { validateExpirationToken, serializeForUri } from './utils';
import i18n from '../i18n';

const BASE_IP = `${process.env.REACT_APP_API_URL}`;
const CLIENT_NAME = 'Petcloud';

export function saveDataOnSession(key, data) {
  return sessionStorage.setItem(key, data);
}

export function saveData(key, data) {
  sessionStorage.setItem(key, data);
  return localStorage.setItem(key, data);
}

export function getData(key) {
  // TODO: localStorage not working on inner webview,
  //  workaround from sessionStorage
  return localStorage.getItem(key) || sessionStorage.getItem(key);
}

// only local storage
export const getDataFromLocalStorage = (key) => {
  const data = localStorage.getItem(key);
  return JSON.parse(data);
};

// only local storage
export const setDataToLocalStorage = (key, data) => {
  localStorage.setItem(key, data);
};

export async function getStorageOrDefault(key) {
  try {
    const data = await getData(key);

    return JSON.parse(data);
  } catch (_) {
    return {};
  }
}

export function removeData(key) {
  return localStorage.removeItem(key);
}

export function removeAllData() {
  sessionStorage.clear();
  return localStorage.clear();
}

export const IDOR_HEADER_V2 = { 'Api-Version': 2 };
export const X_API_KEY_ADMIN = { 'X-Api-Key': 'uLXybXt3jHCugGsr' };
export const API_HEADER_V1 = { 'Api-Version': 'v1' };
export const API_HEADER_V2 = { 'Api-Version': 'v2' };

async function getApiToken(url = '') {
  const fullUrl = `${BASE_IP}/${url}`;
  let tokenKey = 'token';
  const accessToken = await getData('token');

  if (fullUrl.includes(`${BASE_IP}/customer`)) {
    tokenKey = CUSTOMER_API_TOKEN;
  } else if (fullUrl.includes(`${BASE_IP}/claims`)) {
    tokenKey = CLAIMS_API_TOKEN;
  } else if (fullUrl.includes(`${BASE_IP}/policy`)) {
    tokenKey = POLICY_API_TOKEN;
  } else if (fullUrl.includes(`${BASE_IP}/social`)) {
    tokenKey = SOCIAL_API_TOKEN;
  } else if (fullUrl.includes(`${BASE_IP}/purchase`)) {
    tokenKey = PURCHASE_API_TOKEN;
  }

  const token = await getData(tokenKey);

  return token || accessToken;
}

async function call({
  anonymous = false,
  data,
  dispatch,
  extraHeaders = {},
  method,
  responseJson = true,
  returnObject = 'json',
  url = '',
  useGenericError = true,
}) {
  const token = anonymous
    ? 'api-key' : await getApiToken(url);
  const headers = {
    'Accept-Language': i18n.language,
    'Api-Version': 'v1',
    authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Client-Name': CLIENT_NAME,
    'X-Client-Version': app.version,
    ...extraHeaders,
  };

  if (!token) {
    return { devError: 'No token loaded', statusCode: 401, success: false };
  }

  let statusCode;

  try {
    const requestData = {
      body: data && JSON.stringify(data),
      headers,
      method,
    };

    const tokenExpired = validateExpirationToken(token);

    if (!anonymous && tokenExpired) {
      dispatch(sessionExpired());

      return {
        data: { IsValid: false },
        devError: 'Token expired',
        success: false,
      };
    }

    const response = await fetch(`${BASE_IP}/${url}`, requestData);
    statusCode = response.status;

    if (response.status >= 500) {
      const error = await response.json();

      if (useGenericError
        && dispatch
        && returnObject === 'json'
        && !error.detail) {
        dispatch(showGenericError(true));
      }

      return { error, statusCode, success: false };
    }

    if (response.status === 204) {
      return { statusCode, success: true };
    }

    let returnableData = {};
    if (returnObject === 'blob') {
      const res = await response.blob();
      returnableData = { blob: res };
    } else {
      const res = responseJson ? await response.json()
        : { data: await response.text() };
      returnableData = res;

      if (returnableData instanceof Array) {
        return { data: returnableData, statusCode, success: true };
      }
    }

    return { success: response.ok, ...returnableData };
  } catch (error) {
    if (useGenericError && dispatch) {
      dispatch(showGenericError(true));
    }
    return {
      error: error?.message,
      statusCode: statusCode || 500,
      success: false,
    };
  }
}

export function get(dispatch, URL, params = {}) {
  const queryParams = serializeForUri(params);
  const url = `${URL}?${queryParams}`;

  return call({ dispatch, method: 'GET', url });
}

/* api version 2 */
export function getApiV2(dispatch, URL, params = {}) {
  const queryParams = serializeForUri(params);
  const url = `${URL}?${queryParams}`;

  return call({
    dispatch,
    extraHeaders: API_HEADER_V2,
    method: 'GET',
    url,
  });
}

export function postApiV2(
  dispatch,
  url,
  data,
  returnObject = 'json',
  useGenericError = true,
) {
  return call({
    data,
    dispatch,
    extraHeaders: API_HEADER_V2,
    method: 'POST',
    returnObject,
    url,
    useGenericError,
  });
}

export function putApiV2(dispatch, url, data) {
  return call({
    data,
    dispatch,
    extraHeaders: API_HEADER_V2,
    method: 'PUT',
    url,
  });
}

export function delApiV2(dispatch, url, data) {
  return call({
    data,
    dispatch,
    extraHeaders: API_HEADER_V2,
    method: 'DELETE',
    url,
  });
}

export function patchApiV2(
  dispatch,
  url,
  data,
  responseJson = false,
) {
  return call({
    data,
    dispatch,
    extraHeaders: API_HEADER_V2,
    method: 'PATCH',
    responseJson,
    url,
  });
}

export function postApiBlobV2(dispatch, url, data) {
  return call({
    data,
    dispatch,
    extraHeaders: API_HEADER_V2,
    method: 'POST',
    returnObject: 'blob',
    url,
  });
}

/* api IDOR version 2 */
export function getIDORV2(dispatch, path, params = {}) {
  const queryParams = serializeForUri(params);
  const url = `${path}?${queryParams}`;

  return call({
    dispatch,
    extraHeaders: IDOR_HEADER_V2,
    method: 'GET',
    url,
  });
}

export function post(
  dispatch,
  url,
  data,
  returnObject = 'json',
  useGenericError = true,
) {
  return call({
    data,
    dispatch,
    method: 'POST',
    returnObject,
    url,
    useGenericError,
  });
}

export function postBlob(dispatch, url, data) {
  return call({ data, dispatch, method: 'POST', returnObject: 'blob', url });
}

export function postV2(dispatch, url, data, returnObject = 'json') {
  return call({
    data,
    dispatch,
    extraHeaders: IDOR_HEADER_V2,
    method: 'POST',
    returnObject,
    url,
  });
}

/* api IDOR version 2 */
export function postBlobV2(dispatch, url, data) {
  return call({
    data,
    dispatch,
    extraHeaders: IDOR_HEADER_V2,
    method: 'POST',
    returnObject: 'blob',
    url,
  });
}

export function put(dispatch, url, data, formData = false) {
  return call({
    data,
    dispatch,
    method: 'PUT',
    responseJson: formData,
    url,
  });
}

export function del(dispatch, url, data) {
  return call({ data, dispatch, method: 'DELETE', url });
}

export function patch(
  dispatch,
  url,
  data,
  responseJson = false,
) {
  return call({
    data,
    dispatch,
    method: 'PATCH',
    responseJson,
    url,
  });
}

export async function apiCallWithApiKey(
  path,
  params = {},
  dispatch,
  useGenericError = true,
) {
  try {
    let url = `${BASE_IP}/${path}`;

    const options = {
      headers: {
        Authorization: 'APIKEY 8f7ecbb6-a62c-44d9-ab88-7b295ba7956c',
        'Content-Type': 'application/json',
        'X-Client-Name': CLIENT_NAME,
        'X-Client-Version': app.version,
        ...params.headers,
      },
    };

    if (params.params) {
      const queryParams = serializeForUri(params);
      url += `?${queryParams}`;
    }

    if (params.method === 'POST' || params.method === 'DELETE') {
      options.method = params.method;
      options.body = JSON.stringify(params.body);
    }

    const response = await fetch(url, options);
    const res = await response.json();
    const status = response.status;

    if (response.status >= 500) {
      if (dispatch && useGenericError) {
        dispatch(showGenericError(true));
      }
      return {
        ...res,
        status,
        success: false,
      };
    }

    return {
      ...res,
      status,
      success: response.ok,
    };
  } catch (_) {
    if (dispatch) {
      dispatch(showGenericError(true));
    }
    return {
      success: false,
    };
  }
}

export function getAnonymous(
  dispatch,
  URL,
  params = {},
) {
  const queryParams = serializeForUri(params);
  const url = `${URL}?${queryParams}`;

  return call({
    anonymous: true,
    dispatch,
    extraHeaders: API_HEADER_V2,
    method: 'GET',
    url,
  });
}

export function postAnonymous(
  dispatch,
  URL,
  body,
  params = {},
) {
  const queryParams = serializeForUri(params);
  const url = `${URL}?${queryParams}`;

  return call({
    anonymous: true,
    data: body,
    dispatch,
    extraHeaders: API_HEADER_V2,
    method: 'POST',
    url,
  });
}
